import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useTenantFromUrl } from '../hooks/useTenantFromUrl';
import { clientAPI, templateAPI, documentAPI } from '../services/api';
import ContentHeader from './ContentHeader';
import './ClientDocuments.css';

function ClientDocuments() {
  const { id } = useParams();
  const { tenantPath } = useTenantFromUrl();
  const clientId = id;

  const [client, setClient] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadClient();
    loadTemplates();
  }, [id]);

  const loadClient = async () => {
    try {
      const response = await clientAPI.getById(clientId);
      setClient(response.data.data);
    } catch (error) {
      console.error('Error loading client:', error);
      alert('Failed to load client information');
    }
  };

  const loadTemplates = async () => {
    try {
      setLoading(true);
      const response = await templateAPI.getAll({ docType: 'Client' });
      // Handle both response.data.templates and response.data.data for backward compatibility
      const templateList = response.data.templates || response.data.data || [];
      setTemplates(templateList);
    } catch (error) {
      console.error('Error loading templates:', error);
      alert('Failed to load document templates');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerate = async (templateId) => {
    const template = templates.find(t => t.id === templateId);
    if (!template) {
      alert('Template not found');
      return;
    }

    try {
      // Use the API method to fetch the document as a blob
      const response = await documentAPI.generateForClient(clientId, templateId);
      
      // Check if response is actually an error (JSON) instead of a blob
      const contentType = response.headers['content-type'] || response.headers['Content-Type'] || '';
      if (contentType.includes('application/json')) {
        // Response is JSON (error), not a blob
        const errorData = typeof response.data === 'string' 
          ? JSON.parse(response.data) 
          : response.data;
        throw new Error(errorData.error || errorData.message || 'Failed to generate document');
      }
      
      // Extract filename from Content-Disposition header or use default
      // Try both lowercase and original case for header name
      const contentDisposition = response.headers['content-disposition'] || 
                                  response.headers['Content-Disposition'];
      let filename = `${template.displayName}-${clientId}.docx`;
      
      if (contentDisposition) {
        // Try to extract filename from header (handles both quoted and unquoted)
        const filenameMatch = contentDisposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
        if (filenameMatch) {
          // Remove quotes if present
          filename = filenameMatch[1].replace(/['"]/g, '');
        }
      }

      // Create a blob URL and trigger download
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error generating document:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error message:', error.message);
      
      let errorMessage = 'Failed to generate document';
      
      if (error.response?.data) {
        if (typeof error.response.data === 'string') {
          errorMessage = error.response.data;
        } else if (error.response.data.error) {
          errorMessage = error.response.data.error;
        } else if (error.response.data.message) {
          errorMessage = error.response.data.message;
        } else {
          errorMessage = JSON.stringify(error.response.data);
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      alert(`Error: ${errorMessage}`);
    }
  };

  // Filter templates by search term
  const filteredTemplates = templates.filter(template =>
    template.displayName.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Group templates by language, then by category
  const groupedTemplates = filteredTemplates.reduce((groups, template) => {
    const lang = template.language || 'Other';
    const cat = template.category || 'Other';
    
    if (!groups[lang]) {
      groups[lang] = {};
    }
    if (!groups[lang][cat]) {
      groups[lang][cat] = [];
    }
    groups[lang][cat].push(template);
    
    return groups;
  }, {});

  // Sort languages: EN first, ES second, then others
  const sortedLanguages = Object.keys(groupedTemplates).sort((a, b) => {
    if (a === 'EN') return -1;
    if (b === 'EN') return 1;
    if (a === 'ES') return -1;
    if (b === 'ES') return 1;
    return a.localeCompare(b);
  });

  if (loading) {
    return <div className="client-documents">Loading...</div>;
  }

  return (
    <div className="client-documents">
      <ContentHeader
        title="Generate Documents"
        backTo={tenantPath(`/client/${clientId}`)}
        backLabel="Back to Client"
      />

      <div className="documents-section">
        
        {/* Search Box */}
        <div className="search-box">
          <input
            type="text"
            placeholder="Search templates..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
        </div>

        {/* Grouped Template List */}
        {sortedLanguages.length === 0 ? (
          <div className="no-templates">
            <p>No templates found.</p>
          </div>
        ) : (
          <div className="templates-grouped">
            {sortedLanguages.map(language => (
              <div key={language} className="language-group">
                <h4 className="language-header">Language: {language}</h4>
                {Object.keys(groupedTemplates[language]).sort().map(category => (
                  <div key={category} className="category-group">
                    <h5 className="category-header">{category}</h5>
                    <div className="template-rows">
                      {groupedTemplates[language][category].map(template => (
                        <div key={template.id} className="template-row">
                          <div className="template-name">{template.displayName}</div>
                          <button
                            className="generate-btn"
                            onClick={() => handleGenerate(template.id)}
                            type="button"
                          >
                            Generate
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default ClientDocuments;
